<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="missedVisitsReasons"
      :headers="missedVisitsReasonsHeaders"
      :list="missedVisitsReasons"
      @add-new="onAddNewMissedVisitReason"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="missed-visits-reasons"
      clickable
      @table-item-clicked="onEditVisitsMissedReason"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as MissedVisitsReasons)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <MissedVisitsReasonsModalCreateEdit
      v-if="createEditModalActive"
      :missed-visit-reason="missedVisitsReason"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave"></MissedVisitsReasonsModalCreateEdit>
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';

import { getMissedVisitsReasons, removeMissedVisitsReasonsByIds } from '@/services/api/routes';
import { filterActive, filterData, sortData } from '@/utils/table';
import MissedVisitsReasonsModalCreateEdit from '@/components/routes/MissedVisitsReasonsModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { MissedVisitsReasons } from '@/types/Routes';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const missedVisitsReasons = ref<MissedVisitsReasons[]>([]);
const filterIsActive = ref<boolean | undefined>(undefined);

const missedVisitsReasonsHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'sort', text: $t('question.sort'), type: 'text', active: true },
  {
    value: 'dueToSalesPerson',
    text: $t('missedVisitReasons.dueToSalesperson'),
    type: 'check',
    active: true,
  },
  {
    value: 'dueToTraderSite',
    text: $t('missedVisitReasons.dueToTraderSite'),
    type: 'check',
    active: true,
  },
  { value: 'dueToPlan', text: $t('missedVisitReasons.dueToPlan'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData({
    column: 1,
    order: 'asc',
  });
});

const missedVisitsReason = ref<MissedVisitsReasons>(initializeMissedVisitsReason());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeMissedVisitsReason(): MissedVisitsReasons {
  return {
    id: uuidv4(),
    name: '',
    dueToSalesPerson: false,
    dueToTraderSite: false,
    dueToPlan: false,
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewMissedVisitReason() {
  missedVisitsReason.value = initializeMissedVisitsReason();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditVisitsMissedReason(item: MissedVisitsReasons) {
  missedVisitsReason.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

function onSave(modifiedData: MissedVisitsReasons) {
  if (!missedVisitsReasons.value) return;

  const index = missedVisitsReasons.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) missedVisitsReasons.value[index] = modifiedData;
  else missedVisitsReasons.value.push(modifiedData);
  createEditModalActive.value = false;
}

async function loadData(data?: TableFilterData) {
  let response = await getMissedVisitsReasons();
  if (!response) return;

  if (data && data.query) {
    response = filterData(response, data.query, [
      'name',
      'dueToSalesPerson',
      'dueToTraderSite',
      'dueToPlan',
    ]);
  }

  if (data && data.column !== undefined && data.order) {
    response = sortData(response, data.column, data.order, missedVisitsReasonsHeaders);
  }

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  missedVisitsReasons.value = response;
}

async function onDeleteMultiselect(items: MissedVisitsReasons[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (!idsToDelete.length) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeMissedVisitsReasonsByIds(idsToDelete);
  if (!res && missedVisitsReasons.value)
    missedVisitsReasons.value = missedVisitsReasons.value.filter(
      (type) => !idsToDelete.includes(type.id)
    );
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeMissedVisitsReasonsByIds([idToDelete]);
  if (!res && missedVisitsReasons.value)
    missedVisitsReasons.value = missedVisitsReasons.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<MissedVisitsReasons>();
function onDeleteRow(item: MissedVisitsReasons) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (missedVisitsReason.value && !missedVisitsReason.value.isFromManager)
    return `${$t('common.actions.view')} ${$t('missedVisitReasons.missedVisitReason')} `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('missedVisitReasons.missedVisitReason')}`
    : `${$t('common.actions.create')} ${$t('missedVisitReasons.missedVisitReason')}`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
